import { Outlet, useLocation, useNavigate } from "react-router";
import {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { flushSync } from "react-dom";
import {
  ASSISTANT_MAX_WIDTH,
  AssistantPanel,
  PANEL_COVERS_PAGE_QUERY,
  buildAssistantAppState,
} from "@bastion/assistant";
import { SidebarProvider } from "@bastion/sidebar";
import { useDashboardAssistantContextStore, useEpisodeDisplayStore, useEpisodeFiltersStore } from "@bastion/stores";
import { PHONE_QUERY, TOUCH_QUERY, useMediaQuery } from "@bastion/ui";
import { readWorkflowFiltersFromLocation } from "./readWorkflowFilters";
import { flattenEpisodeFiltersForAssistant } from "@bastion/episodes";
import { readEpisodeFiltersFromSearch } from "./readEpisodeFilters";
import { registerWebToolRenderers } from "@/shared/assistant/registerRenderers";
import { AppSidebar } from "./AppSidebar";
import { AppTopBar } from "./AppTopBar";
import { TopBarSlotContext } from "./TopBarActions";
import { CommandPalette } from "./CommandPalette";
import { useAppShortcuts } from "./useAppShortcuts";
import { recordPageVisit } from "./usePageVisits";
import { phoneViewOf, withPhoneView, withoutPhoneView, restorePhonePanelLayout, useClearInitialPhoneView, type PhoneView } from "./phoneViews";
import { useSecondarySidebar } from "./useSecondarySidebar";
import { AppHistoryProvider } from "./useAppHistory";
import {
  CHAT_TAB_ID,
  useRightPanelTabs,
  type RightPanelWorkspaceContext,
} from "./rightPanelWorkspace";
import {
  assistantNavigationRef,
  navigateRef,
  registerWebToolExecutors,
  routeToTabId,
  type AssistantNavigationRequest,
} from "../assistantTools";
import styles from "./AppLayout.module.css";

/** The wide right panel takes this part of the window (`toggleWide`). */
const WIDE_PANEL_SHARE = 0.6;

export function AppLayout() {
  return (
    <SidebarProvider>
      <AppHistoryProvider>
        <AppLayoutInner />
      </AppHistoryProvider>
    </SidebarProvider>
  );
}

function AppLayoutInner() {
  const location = useLocation();
  const routerNavigate = useNavigate();
  const dashboardAssistantContext = useDashboardAssistantContextStore((s) => s.context);
  const isPhone = useMediaQuery(PHONE_QUERY);
  const isTouch = useMediaQuery(TOUCH_QUERY);
  const panelCoversPage = useMediaQuery(PANEL_COVERS_PAGE_QUERY);
  // On a phone the navigation drawer and the right panel open as history
  // entries (phoneViews.ts), so Back and any navigation close them.
  const phoneView = isPhone ? phoneViewOf(location.state) : null;

  const navigate = useCallback((path: string) => {
    routerNavigate(path);
  }, [routerNavigate]);

  const getAppState = useCallback(
    (): Record<string, unknown> => ({
      ...buildAssistantAppState({
        activeTab: routeToTabId(location.pathname),
        workspacePath: "",
        platform: "web",
        episodeFilters: location.pathname.startsWith("/episodes")
          ? {
              ...readEpisodeFiltersFromSearch(location.search),
              currentPage: useEpisodeFiltersStore.getState().currentPage,
            }
          : flattenEpisodeFiltersForAssistant(
              useEpisodeFiltersStore.getState().filters,
              {
                viewMode: useEpisodeDisplayStore.getState().viewMode,
                sort: useEpisodeDisplayStore.getState().sort,
                pageSize: useEpisodeDisplayStore.getState().pageSize,
              },
              useEpisodeFiltersStore.getState().currentPage,
            ),
        workflowFilters: readWorkflowFiltersFromLocation(
          location.pathname,
          location.search,
        ),
        chatSurface: "sidebar",
        dashboardContext: dashboardAssistantContext ?? undefined,
      }),
    }),
    [location.pathname, location.search, dashboardAssistantContext],
  );

  const [assistantOpen, setAssistantOpen] = useState(false);
  const panelOpen = isPhone ? phoneView === "panel" : assistantOpen;
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  // The panel shows the tab last chosen on the page, or the page's Details
  // tab, or chat. A page can leave chat out.
  const {
    tabs: rightPanelTabs,
    chat: rightPanelChat,
    setTabs: setRightPanelTabs,
    activeTabId: activeRightPanelTabId,
    chooseTab: choosePanelTab,
  } = useRightPanelTabs(location.pathname);

  // The values that the callbacks below read. The callbacks keep one
  // identity, so the effects of the pages that use them do not run again.
  // A layout effect updates the values before the effects of the pages run.
  const latestRef = useRef({
    isPhone,
    isTouch,
    location,
    navigate: routerNavigate,
    commandPaletteOpen,
    panelOpen,
    activeRightPanelTabId,
  });
  useLayoutEffect(() => {
    latestRef.current = {
      isPhone,
      isTouch,
      location,
      navigate: routerNavigate,
      commandPaletteOpen,
      panelOpen,
      activeRightPanelTabId,
    };
  });

  /** Chooses the tab of the right panel on the current page. */
  const setActiveRightPanelTabId = useCallback(
    (tabId: string) => choosePanelTab(latestRef.current.location.pathname, tabId),
    [choosePanelTab],
  );

  /**
   * Opens a phone view as a new history entry with the same URL. A view that
   * is open gives its entry to the new view.
   */
  const openPhoneView = useCallback((view: PhoneView) => {
    const latest = latestRef.current;
    const current = phoneViewOf(latest.location.state);
    if (current === view) return;
    const state = withPhoneView(latest.location.state, view);
    latest.navigate(
      {
        pathname: latest.location.pathname,
        search: latest.location.search,
        hash: latest.location.hash,
      },
      { replace: current !== null, state },
    );
    // The router renders the new entry in a transition. Until then a second
    // tap reads the view as open.
    latest.location = { ...latest.location, state };
  }, []);

  /** Closes a phone view: Back to the entry under it. */
  const closePhoneView = useCallback((view: PhoneView) => {
    const latest = latestRef.current;
    if (phoneViewOf(latest.location.state) !== view) return;
    latest.navigate(-1);
    latest.location = { ...latest.location, state: withoutPhoneView(latest.location.state) };
  }, []);

  /** Opens or closes the right panel on a phone. Wider, it is React state. */
  const setPhonePanelOpen = useCallback(
    (open: boolean) => {
      if (open) openPhoneView("panel");
      else closePhoneView("panel");
    },
    [closePhoneView, openPhoneView],
  );

  const [assistantWidth, setAssistantWidth] = useState<number | null>(null);
  // The width before the panel became wide, or null while it is not wide.
  const [widthBeforeWide, setWidthBeforeWide] = useState<{ width: number | null } | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [panelHandoffSessionId, setPanelHandoffSessionId] = useState<string | null>(null);
  const toggleAssistant = useCallback(() => {
    if (latestRef.current.isPhone) {
      setPhonePanelOpen(phoneViewOf(latestRef.current.location.state) !== "panel");
      return;
    }
    setAssistantOpen((isOpen) => !isOpen);
  }, [setPhonePanelOpen]);

  const openRightPanelTab = useCallback((tabId: string) => {
    setActiveRightPanelTabId(tabId);
    if (latestRef.current.isPhone) {
      setPhonePanelOpen(true);
      return;
    }
    setAssistantOpen(true);
  }, [setActiveRightPanelTabId, setPhonePanelOpen]);

  // A page that unmounts can call this after a navigation. On a phone the
  // new entry has no panel, so the call then does nothing.
  const closeRightPanel = useCallback(() => {
    if (latestRef.current.isPhone) {
      setPhonePanelOpen(false);
      return;
    }
    setAssistantOpen(false);
  }, [setPhonePanelOpen]);

  // A second press on the open tab closes the panel, so a hotkey like R can
  // both summon and dismiss the Review tab.
  const toggleRightPanelTab = useCallback(
    (tabId: string) => {
      const latest = latestRef.current;
      // On a phone the entry state shows a sheet that opens in a transition.
      const isOpen = latest.isPhone
        ? phoneViewOf(latest.location.state) === "panel"
        : latest.panelOpen;
      if (isOpen && latest.activeRightPanelTabId === tabId) {
        closeRightPanel();
        return;
      }
      openRightPanelTab(tabId);
    },
    [closeRightPanel, openRightPanelTab],
  );

  // toggleWide reads the latest dragged width without changing its callback.
  const assistantWidthRef = useRef(assistantWidth);
  useEffect(() => {
    assistantWidthRef.current = assistantWidth;
  }, [assistantWidth]);

  const toggleWide = useCallback(() => {
    if (widthBeforeWide) {
      setAssistantWidth(widthBeforeWide.width);
      setWidthBeforeWide(null);
    } else {
      setWidthBeforeWide({ width: assistantWidthRef.current });
      setAssistantWidth(
        Math.min(ASSISTANT_MAX_WIDTH, Math.round(window.innerWidth * WIDE_PANEL_SHARE)),
      );
    }
  }, [widthBeforeWide]);

  const applyPanelLayout = useCallback((layout: { open: boolean; tab: string; wide: boolean; width?: number | null }) => {
    const latest = latestRef.current;
    setActiveRightPanelTabId(layout.tab);
    setAssistantWidth(layout.width ?? (layout.wide
      ? Math.max(280, Math.min(ASSISTANT_MAX_WIDTH, Math.round(window.innerWidth * WIDE_PANEL_SHARE)))
      : null));
    setWidthBeforeWide((previous) => layout.wide ? (previous ?? { width: null }) : null);
    setAssistantOpen(layout.open);
    if (!latest.isPhone) return;
    const state = restorePhonePanelLayout(latest.location, layout.open, latest.navigate);
    if (state) latest.location = { ...latest.location, state };
  }, [setActiveRightPanelTabId]);

  const rightPanelContext = useMemo<RightPanelWorkspaceContext>(
    () => ({
      setTabs: setRightPanelTabs,
      openTab: openRightPanelTab,
      closePanel: closeRightPanel,
      toggleTab: toggleRightPanelTab,
      activeTabId: activeRightPanelTabId,
      isOpen: panelOpen,
      isWide: widthBeforeWide !== null,
      toggleWide,
      width: assistantWidth,
      isResizing,
      applyLayout: applyPanelLayout,
    }),
    [
      activeRightPanelTabId,
      panelOpen,
      closeRightPanel,
      openRightPanelTab,
      setRightPanelTabs,
      toggleRightPanelTab,
      toggleWide,
      widthBeforeWide,
      assistantWidth,
      isResizing,
      applyPanelLayout,
    ],
  );

  const handleAssistantNavigation = useCallback(
    (request: AssistantNavigationRequest) => {
      routerNavigate(request.targetPath);
      request.afterNavigate?.();
      setPanelHandoffSessionId(request.sessionIdToHandoff);
      // The chat continues on the page that Gauss opened.
      choosePanelTab(request.targetPath, CHAT_TAB_ID);
      // On a phone the panel would cover the page that Gauss opened. The
      // handoff waits until the reader opens the panel again.
      if (latestRef.current.isPhone) return;
      setAssistantOpen(true);
    },
    [choosePanelTab, routerNavigate],
  );

  useEffect(() => {
    registerWebToolExecutors();
    registerWebToolRenderers();
    navigateRef.current = navigate;
    assistantNavigationRef.current = handleAssistantNavigation;
    return () => {
      if (navigateRef.current === navigate) {
        navigateRef.current = null;
      }
      if (assistantNavigationRef.current === handleAssistantNavigation) {
        assistantNavigationRef.current = null;
      }
    };
  }, [handleAssistantNavigation, navigate]);

  const handlePanelHandoffConsumed = useCallback(() => {
    setPanelHandoffSessionId(null);
  }, []);

  const handleViewAllChats = useCallback(() => {
    if (!latestRef.current.isPhone) {
      navigate("/");
      return;
    }
    // On a phone the chat list is in the navigation drawer of the home
    // page. Home replaces the entry of the panel, and the drawer opens over
    // it, so Back closes the drawer first.
    routerNavigate("/", { replace: true });
    routerNavigate("/", { state: withPhoneView(null, "sideNav") });
  }, [navigate, routerNavigate]);

  // On a phone the open palette also holds a history entry, so Back closes
  // it. Its open state stays React state: the palette renders in the same
  // task as the tap, so iOS raises the keyboard for its input.
  // "pushed": the entry is added. "shown": the location shows the entry.
  const paletteEntryRef = useRef<"none" | "pushed" | "shown">("none");

  const closeCommandPalette = useCallback(() => {
    setCommandPaletteOpen(false);
    if (paletteEntryRef.current !== "none") {
      paletteEntryRef.current = "none";
      closePhoneView("palette");
    }
  }, [closePhoneView]);

  const toggleCommandPalette = useCallback(() => {
    const { isPhone: phone, isTouch: touch, commandPaletteOpen: open } = latestRef.current;
    if (!phone && !touch) {
      setCommandPaletteOpen((isOpen) => !isOpen);
      return;
    }
    if (open) {
      closeCommandPalette();
      return;
    }
    // iOS raises the keyboard only for a focus in the task of the tap, so
    // the palette and its input render now.
    flushSync(() => setCommandPaletteOpen(true));
    if (phone) {
      paletteEntryRef.current = "pushed";
      openPhoneView("palette");
    }
  }, [closeCommandPalette, openPhoneView]);

  useAppShortcuts(toggleCommandPalette);

  // Home's shortcuts come from the pages this browser opens.
  useEffect(() => {
    recordPageVisit(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (paletteEntryRef.current === "none") return;
    if (phoneViewOf(location.state) === "palette") {
      paletteEntryRef.current = "shown";
    } else if (paletteEntryRef.current === "shown") {
      // Back left the entry of the palette.
      paletteEntryRef.current = "none";
      setCommandPaletteOpen(false);
    }
  }, [location.state]);

  const navigateFromCommandPalette = useCallback(
    (path: string) => {
      if (paletteEntryRef.current === "none") {
        navigate(path);
        return;
      }
      // The page takes the entry of the palette, so Back skips the palette.
      paletteEntryRef.current = "none";
      routerNavigate(path, { replace: true });
    },
    [navigate, routerNavigate],
  );

  const { isOpen: savedSecondarySidebarOpen, toggle: toggleSecondarySidebar } =
    useSecondarySidebar();
  // On a phone the sidebar is in the navigation drawer. Wider, it
  // keeps the saved toggle.
  const navDrawerOpen = phoneView === "sideNav";
  const secondarySidebarOpen = isPhone ? navDrawerOpen : savedSecondarySidebarOpen;

  const toggleSideNav = useCallback(() => {
    if (!latestRef.current.isPhone) {
      toggleSecondarySidebar();
      return;
    }
    if (phoneViewOf(latestRef.current.location.state) === "sideNav") {
      closePhoneView("sideNav");
    } else {
      openPhoneView("sideNav");
    }
  }, [closePhoneView, openPhoneView, toggleSecondarySidebar]);

  const closeNavDrawer = useCallback(() => closePhoneView("sideNav"), [closePhoneView]);

  // A link in the open drawer takes the entry of the drawer, so Back from
  // the new page does not open the drawer again.
  const navigateFromSideNav = useCallback(
    (path: string) => {
      const { isPhone: phone, location: current } = latestRef.current;
      if (phone && phoneViewOf(current.state) === "sideNav") {
        routerNavigate(path, { replace: true });
        return;
      }
      navigate(path);
    },
    [navigate, routerNavigate],
  );

  useClearInitialPhoneView();

  // A layer over the page takes the focus from the page (inert). Where the
  // right panel is a drawer, the top bar stays live. The phone sheet and the
  // navigation drawer cover the top bar too.
  const panelIsLayer = panelOpen && panelCoversPage;
  const pageInert = navDrawerOpen || panelIsLayer;
  const headerInert = navDrawerOpen || (isPhone && panelOpen);

  // Esc closes the top layer: the navigation drawer, then the right panel
  // where it covers the page. A menu or a dialog that handles Esc marks the
  // event, and the event then closes only that menu or dialog.
  useEffect(() => {
    if (!navDrawerOpen && !panelIsLayer) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      // Native select pickers use Escape without marking the event handled.
      if (event.target instanceof HTMLElement && event.target.closest("select")) return;
      if (navDrawerOpen) closeNavDrawer();
      else closeRightPanel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeNavDrawer, closeRightPanel, navDrawerOpen, panelIsLayer]);

  const hasAssistantHandoff = panelHandoffSessionId !== null && !panelOpen;
  const [topBarLeading, setTopBarLeading] = useState<HTMLElement | null>(null);
  const [topBarTrailing, setTopBarTrailing] = useState<HTMLElement | null>(null);
  const topBarSlots = useMemo(
    () => ({
      leading: isPhone ? null : topBarLeading,
      trailing: isPhone ? null : topBarTrailing,
      page: topBarTrailing,
    }),
    [isPhone, topBarLeading, topBarTrailing],
  );

  return (
    <div
      className={styles.shell}
      data-testid="app-shell"
      data-layout={isPhone ? "phone" : "sidebar"}
      data-sidebar={secondarySidebarOpen ? "open" : "closed"}
    >
      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={closeCommandPalette}
        onNavigate={navigateFromCommandPalette}
      />
      <div className={styles.body}>
        {isPhone ? (
          <div
            className={styles.sideNav}
            data-open={navDrawerOpen ? "true" : "false"}
            inert={panelIsLayer}
          >
            <AppSidebar
              pathname={location.pathname}
              inDrawer
              onToggle={closeNavDrawer}
              onOpenSearch={toggleCommandPalette}
              onNavigate={navigateFromSideNav}
            />
          </div>
        ) : secondarySidebarOpen ? (
          <AppSidebar
            pathname={location.pathname}
            onToggle={toggleSideNav}
            onOpenSearch={toggleCommandPalette}
            onNavigate={navigateFromSideNav}
          />
        ) : null}
        {navDrawerOpen ? (
          <button
            type="button"
            className={styles.navScrim}
            onClick={closeNavDrawer}
            aria-label="Close navigation"
          />
        ) : null}
        <div className={styles.column}>
          <AppTopBar
            pathname={location.pathname}
            isSidebarOpen={secondarySidebarOpen}
            isPhone={isPhone}
            inert={headerInert}
            onToggleSidebar={toggleSideNav}
            onOpenSearch={toggleCommandPalette}
            isAssistantOpen={panelOpen}
            onAssistantToggle={toggleAssistant}
            hasAssistantHandoff={hasAssistantHandoff}
            onLeadingSlot={setTopBarLeading}
            onActionsSlot={setTopBarTrailing}
          />
          <div className={styles.row}>
            <div className={styles.main} inert={pageInert}>
              <main className={styles.content}>
                <TopBarSlotContext.Provider value={topBarSlots}>
                  <Outlet context={rightPanelContext} />
                </TopBarSlotContext.Provider>
              </main>
            </div>
            <AssistantPanel
              isOpen={panelOpen}
              onToggle={toggleAssistant}
              width={assistantWidth}
              onWidthChange={setAssistantWidth}
              isResizing={isResizing}
              onResizingChange={setIsResizing}
              getAppState={getAppState}
              dashboardAssistantActive={Boolean(dashboardAssistantContext)}
              dashboardAssistantLabel={dashboardAssistantContext?.title ?? null}
              handoffSessionId={panelHandoffSessionId}
              onHandoffConsumed={handlePanelHandoffConsumed}
              onViewAllChats={handleViewAllChats}
              extraTabs={rightPanelTabs}
              showChat={rightPanelChat}
              activeTabId={activeRightPanelTabId}
              onActiveTabChange={setActiveRightPanelTabId}
              isWide={widthBeforeWide !== null}
              onToggleWide={toggleWide}
              overlayBelowWide
            />
          </div>
        </div>
      </div>
    </div>
  );
}
